import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Phase 4b — buyer preference memory.
 *
 * Small keyed store for "how I like to look at the catalog": price unit, mass
 * unit, home regions, view mode, sort. Two layers, on purpose:
 *
 *  1. localStorage — synchronous, so the first paint never flickers a default
 *     and the feature still works while the row is loading or offline.
 *  2. `public.user_preferences` JSONB — durable and cross-device, so a buyer
 *     who set things up on a laptop finds them on their phone. This is the
 *     "investment" rung: switching away means re-teaching a new tool.
 *
 * Writes are optimistic and debounced. A failed sync is never surfaced — the
 * local value already applied, and the next write retries the whole object.
 */

const LS_PREFIX = 'cfx_pref:';
const SYNC_DEBOUNCE_MS = 800;

/** Loaded server snapshot, shared across all hook instances. */
let remoteCache: Record<string, unknown> | null = null;
let loadPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

function readLocal<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    return raw == null ? undefined : (JSON.parse(raw) as T);
  } catch {
    return undefined;
  }
}

function writeLocal(key: string, value: unknown) {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(value));
  } catch {
    /* private mode / quota — local mirror is best-effort */
  }
}

async function loadRemote(): Promise<void> {
  if (remoteCache) return;
  if (!loadPromise) {
    loadPromise = (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth?.user) {
        remoteCache = {};
        return;
      }
      const { data, error } = await supabase
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', auth.user.id)
        .maybeSingle();
      if (error) console.error('preferences load failed:', error);
      remoteCache = (data?.preferences as Record<string, unknown>) ?? {};
      // Server wins on first load so a second device adopts the saved setup.
      for (const [k, v] of Object.entries(remoteCache)) writeLocal(k, v);
      listeners.forEach((fn) => fn());
    })();
  }
  return loadPromise;
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;

function queueSync() {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(async () => {
    syncTimer = null;
    const payload = remoteCache;
    if (!payload) return;
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) return;
    const { error } = await supabase
      .from('user_preferences')
      .upsert({ user_id: auth.user.id, preferences: payload as any }, { onConflict: 'user_id' });
    if (error) console.error('preferences save failed:', error);
  }, SYNC_DEBOUNCE_MS);
}

/**
 * Remembered value for one preference key.
 * Reads local first (synchronous), then adopts the server value once loaded.
 */
export function usePreference<T>(key: string, fallback: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => readLocal<T>(key) ?? fallback);
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  useEffect(() => {
    let alive = true;
    const adopt = () => {
      if (!alive || !remoteCache) return;
      if (key in remoteCache) setValue(remoteCache[key] as T);
    };
    listeners.add(adopt);
    void loadRemote().then(adopt);
    return () => {
      alive = false;
      listeners.delete(adopt);
    };
  }, [key]);

  const set = useCallback(
    (next: T) => {
      setValue(next);
      writeLocal(key, next);
      remoteCache = { ...(remoteCache ?? {}), [key]: next };
      queueSync();
    },
    [key],
  );

  return [value, set];
}

/**
 * Fresh cross-device read of one key: always asks the server (another device may have
 * written since this tab loaded), then updates the shared cache and local mirror.
 */
export async function loadPreferenceFresh<T>(key: string): Promise<T | undefined> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth?.user) return readLocal<T>(key);
  const { data, error } = await supabase
    .from('user_preferences')
    .select('preferences')
    .eq('user_id', auth.user.id)
    .maybeSingle();
  if (error) return readLocal<T>(key);
  const prefs = (data?.preferences as Record<string, unknown>) ?? {};
  remoteCache = { ...(remoteCache ?? {}), ...prefs };
  if (key in prefs) writeLocal(key, prefs[key]); else try { localStorage.removeItem(LS_PREFIX + key); } catch { /* best-effort */ }
  return prefs[key] as T | undefined;
}

/** Writes one key now (no debounce) — for state another device must see moments later. */
export function savePreferenceNow(key: string, value: unknown) {
  writeLocal(key, value);
  remoteCache = { ...(remoteCache ?? {}), [key]: value };
  void loadRemote().then(() => {
    remoteCache = { ...(remoteCache ?? {}), [key]: value };
    void queueSyncNow();
  });
}

async function queueSyncNow() {
  if (syncTimer) { clearTimeout(syncTimer); syncTimer = null; }
  const payload = remoteCache;
  if (!payload) return;
  const { data: auth } = await supabase.auth.getUser();
  if (!auth?.user) return;
  const { error } = await supabase
    .from('user_preferences')
    .upsert({ user_id: auth.user.id, preferences: payload as any }, { onConflict: 'user_id' });
  if (error) console.error('preferences save failed:', error);
}

/** Canonical keys, so two screens never remember the same thing twice. */
export const PREF_KEYS = {
  priceUnit: 'catalog.priceUnit',
  massUnit: 'catalog.massUnit',
  viewMode: 'catalog.viewMode',
  sort: 'catalog.sort',
  regions: 'catalog.regions',
  dismissedLogoNudge: 'branding.dismissedLogoNudge',
  deskResume: 'desk.resume',
  notificationSound: 'notifications.sound',
  dismissedDemand: 'buyRequests.dismissedDemand',
} as const;
